import DummyFooterNavigationData from '@allesauto/dummy/navigationHeader.json';
import translate from '@allesauto/i18n';
import { NavigationIconLinkType } from '@allesauto/utils/consts';
import { isProtectedRoute } from '@allesauto/utils/funcs';
import { Routes_DE as Routes } from '@allesauto/utils/routes';
import { Popover, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React, { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import Button from '../button';
import CircularLoader, {
  CircularLoaderSize,
  CircularLoaderType,
} from '../circularLoader';
import LoaderOverlay from '../loaderOverlay';
import NavigationIconLink from '../navigationIconLink';
import NextLink from '../nextLink';
import TextLink from '../textLink';
import { useWhiteLabel } from '../whiteLabel';

interface HeaderUserMenuProps {
  link?: string;
  icon?: ReactNode;
  isUserIcon?: boolean;
  isLogged?: boolean;
  hasMessageNotification?: boolean;
  handleHover: (flag: boolean) => void;
  hideCartNotification?: () => void;
  open: boolean;
  onLogout?: () => void;
  className: string;
}

const HeaderUserMenu: FC<HeaderUserMenuProps> = ({
  link,
  icon,
  isLogged = false,
  isUserIcon = false,
  hasMessageNotification = false,
  handleHover,
  hideCartNotification,
  open,
  onLogout,
  className: popoverGroupClass,
}) => {
  const intl = useIntl();
  const router = useRouter();
  const whitelabel = useWhiteLabel();
  const [loading, setLoading] = useState<string | undefined>();
  const accountLinkLength = DummyFooterNavigationData.accountLinks.length - 1;
  // Get dummy data to populate sidebar
  const accountLinks = DummyFooterNavigationData.accountLinks.slice(
    2,
    accountLinkLength
  );
  const logoutLink = DummyFooterNavigationData.accountLinks[accountLinkLength];

  useEffect(() => {
    const handleRouteChangeComplete = () => {
      setLoading(undefined);
    };

    router.events.on('routeChangeComplete', handleRouteChangeComplete);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [router]);

  const className = {
    popover: 'flex items-center p-[12px] md:p-0 h-full lg:pt-0 ml-auto',
    popoverButton:
      'border-b-7px duration-200 ease-out flex font-normal font-regular items-center pt-px relative text-p1 text-purple80 monochrome:text-grey90 transition-colors z-20 cursor-pointer h-full',
    linkContainer: clsx(
      '!-mb-7px flex m-0 relative w-1/3',
      isUserIcon
        ? 'sm:hidden md:block lg:flex lg:h-[calc(100%-7px)]'
        : 'flex h-[calc(100%-7px)]'
    ),
    notificationLink:
      'flex items-center p-[12px] md:p-0 border-b-7px border-transparent ml-auto',
    notification: clsx(
      '!leading-5 -right-[18px] absolute bg-red60 h-10px rounded-circle text-[12px] text-center text-white w-10px md:-top-[5px] lg:top-[7px]'
    ),
    popoverPanel:
      'absolute inset-x-0 top-[69px] bg-white w-[25.1%] flex left-[70.82%]',
    popoverPanelInner:
      'bg-white border-1px border-grey10 flex-col overflow-hidden pt-20px px-20px relative w-full sm:hidden lg:flex',
    title:
      'font-headline font-weight-bold mb-20px text-headline4 text-grey100 text-left',
  };

  const goToLoginPage = useCallback(async () => {
    setLoading('loginBtn');
    if (
      router.asPath.replace(/\//g, '') ===
      Routes.SHOPPING_CART.replace(/\//g, '')
    ) {
      await router.push(
        `${Routes.ACCOUNT.LOGIN}?redirectTo=${encodeURIComponent(
          Routes.SHOPPING_CART
        )}`
      );
    } else {
      await router.push(Routes.ACCOUNT.LOGIN);
    }

    setLoading(undefined);
  }, [router]);

  const handleHoverOverlay = useCallback(
    (value: boolean) => {
      hideCartNotification && hideCartNotification();
      handleHover(value);
    },
    [handleHover, hideCartNotification]
  );

  const handleOpenOverlay = useCallback(() => {
    handleHoverOverlay(true);
  }, [handleHoverOverlay]);

  const handleLinkClick = useCallback(
    (value: string) => () => {
      setLoading(value);
    },
    []
  );

  const handleLogout = useCallback(
    (value: string) => () => {
      onLogout?.();
      setLoading(value);
      handleHoverOverlay(false);
      if (isProtectedRoute(router.asPath)) {
        router.push(Routes.ROOT);
      }
      setLoading(undefined);
    },
    [handleHoverOverlay, onLogout]
  );

  return (
    <>
      {loading !== undefined && (
        <LoaderOverlay
          customClassName={{
            containerCustomClass: 'fixed top-[70px] h-screen w-screen',
          }}
          hasLoader={false}
          hasZeroOpacity
        />
      )}
      <Popover.Group
        onMouseLeave={() => {
          loading === undefined && handleHoverOverlay(false);
        }}
        className={popoverGroupClass}
      >
        <Popover className={className.popover}>
          <div
            className={clsx(
              open
                ? 'border-green70 monochrome:border-grey70 wl-border-highlight'
                : 'border-transparent hover:border-green70 monochrome:hover:border-grey70 wl-border-highlight--hover hover:border-b-7px',
              className.popoverButton
            )}
            onKeyPress={handleOpenOverlay}
            tabIndex={0}
            onMouseEnter={handleOpenOverlay}
          >
            <div className={className.linkContainer}>
              {link && (
                <NextLink
                  href={link}
                  passHref
                  className={className.notificationLink}
                >
                  {icon}
                  {hasMessageNotification && (
                    <span className={className.notification}>{''}</span>
                  )}
                </NextLink>
              )}
            </div>
          </div>
          <Popover.Panel static className={className.popoverPanel}>
            <Transition
              enter="transition-[max-height] duration-250 ease"
              enterFrom="!max-h-0 overflow-hidden"
              enterTo="overflow-hidden max-h-[450px]"
              leave="transition-[max-height] duration-100 ease"
              leaveFrom="overflow-hidden max-h-[450px]"
              leaveTo="!max-h-0 overflow-hidden"
              show={open}
              className={className.popoverPanelInner}
            >
              {isLogged ? (
                <>
                  <p className={className.title}>
                    {translate(intl, 'ui.HEADER.USER_CART.PERSONAL_ACCOUNT')}
                  </p>
                  {accountLinks.map((accountLink) => (
                    <NavigationIconLink
                      icon={accountLink.iconType as NavigationIconLinkType}
                      name={accountLink.name}
                      href={accountLink.href}
                      key={accountLink.id}
                      isHeaderNavigation={false}
                      isHeaderUserMenu={true}
                      isLoading={loading === accountLink.name}
                      onClickHandler={handleLinkClick(accountLink.name)}
                    />
                  ))}
                  <NavigationIconLink
                    icon={logoutLink.iconType as NavigationIconLinkType}
                    name={logoutLink.name}
                    href="#"
                    isHeaderNavigation={false}
                    isHeaderUserMenu={true}
                    isLoading={loading === logoutLink.name}
                    onClickHandler={handleLogout(logoutLink.name)}
                  />
                </>
              ) : (
                <>
                  <Button
                    fullWidth={true}
                    onClick={goToLoginPage}
                    whitelabel={!!whitelabel}
                  >
                    {loading === 'loginBtn' ? (
                      <CircularLoader
                        type={CircularLoaderType.primary}
                        customSize={CircularLoaderSize.sm}
                      />
                    ) : (
                      translate(intl, 'ui.HEADER.USER_CART.TO_LOGIN_PAGE')
                    )}
                  </Button>
                  <div className="fff flex items-center py-20px">
                    <p className="pr-[12px]">
                      {translate(intl, 'ui.HEADER.USER_CART.NO_ACCOUNT_TEXT')}
                    </p>
                    <NextLink
                      href={
                        router.asPath.replace(/\//g, '') ===
                        Routes.SHOPPING_CART.replace(/\//g, '')
                          ? `${
                              Routes.ACCOUNT.REGISTER
                            }?redirectTo=${encodeURIComponent(
                              Routes.SHOPPING_CART
                            )}`
                          : Routes.ACCOUNT.REGISTER
                      }
                      passHref
                      legacyBehavior
                    >
                      <TextLink
                        isLoading={loading === 'registerBtn'}
                        text={translate(
                          intl,
                          'ui.HEADER.USER_CART.REGISTER_NOW'
                        )}
                        onClick={handleLinkClick('registerBtn')}
                      />
                    </NextLink>
                  </div>
                </>
              )}
            </Transition>
          </Popover.Panel>
        </Popover>
      </Popover.Group>
    </>
  );
};

export default HeaderUserMenu;
